// ---------------------------------------------------------------------------
// Flow Editor Store — combines all slices into a single Zustand store.
// ---------------------------------------------------------------------------

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { clearUndoHistory } from '@/mcp/init-undo'
import { createFlowSlice, type FlowSlice } from './flow-slice'
import { createUiSlice, type UiSlice } from './flow-ui-slice'

export type FlowEditorState = FlowSlice & UiSlice & {
  /**
   * Id of the project the store content belongs to. The flow-editor store is
   * a module singleton that outlives in-SPA project switches — see
   * `ensureProject` for why this matters. Managed exclusively by
   * `ensureProject` (`reset` clears content but keeps the scope).
   */
  projectId: string | null
  reset: () => void
  /**
   * Scope the store to a project, resetting ALL flow-editor state when the
   * project changes. Without this, switching project A → B inside the SPA
   * left A's flows in the store; the Behavior surface hydration then inferred
   * them as "editor-only drafts" of B (list/pill pollution), and one step
   * edit would persist A's flow into B's FIRST block via the auto-save
   * bridge's fallback routing — a cross-project write. Called by every
   * project open/close path in project-slice and (defense in depth) by the
   * Behavior surface hydration. No-op when the project is unchanged, so
   * unsaved drafts survive re-renders and re-opens of the same project.
   */
  ensureProject: (projectId: string | null) => void
}

export const useFlowEditorStore = create<FlowEditorState>()(
  immer((set, get, api) => ({
    ...createFlowSlice(set as never, get as never, api),
    ...createUiSlice(set as never, get as never, api),

    projectId: null,

    reset: () => {
      set((state) => {
        state.flows = []
        state.selectedFlowId = null
        // Per-flow canvas/diagram caches are keyed by flow id — `<block>:<name>`
        // ids can COLLIDE across projects (two projects with a
        // `todo-block:addTodo`), so a project-scoped reset must drop them too.
        state.flowDataMap = {}
        state.diagramMap = {}
        state.nodes = []
        state.connections = []
        state.nextNodeId = 1
        state.selectedNodeIds = []
        state.canUndo = false
        state.canRedo = false
        state.flowSteps = []
        state.flowStepsDirty = false
        state.flowStepsFlowId = null
        state.leftPanelOpen = true
        state.rightPanelOpen = true
        state.activeLeftTab = 'event-flow'
        state.hoveredNodeId = null
        state.highlightedFlowId = null
        state.highlightedEventId = null
        state.highlightedComponentId = null
        state.selectedEventContext = null
        state.selectedStepIndex = null
        state.selectedStepPath = null
        state.inspectorOpen = false
      })
    },

    ensureProject: (projectId: string | null) => {
      if (get().projectId === projectId) return
      // PI-3 — clear the undo/redo stacks on every project switch. The undo
      // middleware's stacks are session-lifetime module state while hydrated
      // flow ids (`<block>:<name>`) COLLIDE across template-derived projects
      // (see the flowDataMap comment in `reset` above): without this, Cmd+Z
      // in project B replays — and PERSISTS, via restoreFlowSteps →
      // recoverFlowSteps → the auto-save bridge — project A's snapshots into
      // B's blocks. ensureProject is the single choke point: EVERY project
      // open/close path funnels through it (project-slice openProject /
      // openServerProject / openAboxApp / closeProject / clearProjectToEmpty
      // / the createServerProject local fallback, register-studio-tools'
      // project switch, and the Behavior-surface hydration defense-in-depth)
      // and it no-ops when the project is unchanged — so the stacks are
      // scoped to exactly one project without ever clearing mid-session.
      clearUndoHistory()
      get().reset()
      set((state) => {
        state.projectId = projectId
      })
    },
  })),
)

// Phase 378 Plan 02 — expose the flow editor store on window under VITE_E2E_MOCK
// so Playwright can drive step CRUD via page.evaluate(). Gated by build-time
// env so the attachment never ships to production bundles.
if (typeof window !== 'undefined' && import.meta.env.VITE_E2E_MOCK === 'true') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).__COGO_FLOW_EDITOR__ = useFlowEditorStore
}
